import { PlusIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useCallback, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type { GroupResponseDto } from '@splitfiesta/shared';
import { Button } from '@splitfiesta/ui';

import { useGroupsWithBalancesQuery } from '@/hooks/queries/useGroups';
import {
  useCommonShortcuts,
  useKeyboardShortcut,
} from '@/hooks/useKeyboardShortcut';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { GroupSelectorModal } from './GroupSelectorModal';

export function QuickExpenseButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isOnline } = useNetworkStatus();

  // Fetch all groups with balances - uses same query as groups list page (no duplicate API calls)
  const { data, isLoading, isError } = useGroupsWithBalancesQuery();

  // Use query data or fallback to cached data when offline
  const groups = useMemo(() => {
    // First try to use query data
    if (data && data.length > 0) {
      return data;
    }

    // If no data and we're offline/error, try to get cached data
    if (isError || !isOnline) {
      // First try React Query's in-memory cache
      const cachedQueries = queryClient.getQueriesData<GroupResponseDto[]>({
        queryKey: [...queryKeys.groups(), 'list'],
      });

      for (const [, queryData] of cachedQueries) {
        if (queryData && queryData.length > 0) {
          return queryData;
        }
      }

      // Fallback: Read directly from localStorage if in-memory cache is empty
      // This handles the case where page is refreshed while offline
      try {
        const persistedCache = localStorage.getItem('splitfiesta-query-cache');
        if (persistedCache) {
          const parsed = JSON.parse(persistedCache);
          const groupsQuery = parsed.clientState?.queries?.find(
            (q: { queryKey: unknown[] }) =>
              q.queryKey[0] === 'splitfiesta' &&
              q.queryKey[1] === 'groups' &&
              q.queryKey[2] === 'list'
          );
          if (groupsQuery?.state?.data && groupsQuery.state.data.length > 0) {
            return groupsQuery.state.data as GroupResponseDto[];
          }
        }
      } catch {
        // Silently fail - just use empty array if cache read fails
      }
    }

    return [];
  }, [data, isError, isOnline, queryClient]);

  const handleQuickExpense = useCallback(() => {
    if (groups.length === 0) {
      // No groups, redirect to create group
      navigate('/groups/create');
    } else if (groups.length === 1) {
      // Single group, go directly to expense creation
      navigate(`/groups/${groups[0].id}/expenses/create`);
    } else {
      // Multiple groups - show selector modal
      setIsModalOpen(true);
    }
  }, [groups, navigate]);

  const handleGroupSelect = useCallback(
    (group: GroupResponseDto) => {
      setIsModalOpen(false);
      navigate(`/groups/${group.id}/expenses/create`);
    },
    [navigate]
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Get keyboard shortcut info
  const { modifierKey, modifierSymbol } = useCommonShortcuts();

  // Register keyboard shortcut (Cmd+E or Ctrl+E)
  useKeyboardShortcut(handleQuickExpense, {
    key: 'e',
    modifiers: [modifierKey],
    enabled: !isModalOpen && !isLoading,
  });

  return (
    <>
      <Button
        variant="primary"
        leftIcon={<PlusIcon className="w-4 h-4" />}
        onClick={handleQuickExpense}
        disabled={isLoading && groups.length === 0}
        className="w-full sm:w-auto whitespace-nowrap"
        data-testid="quick-create-expense"
        title={`${t('expenses:actions.addExpense')} (${modifierSymbol}+E)`}
        aria-label={t('expenses:actions.addExpense')}
        aria-keyshortcuts={`${modifierSymbol}+E`}
      >
        <span className="flex items-center gap-2">
          {t('expenses:actions.addExpense')}
          <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-sm">
            {modifierSymbol}+E
          </kbd>
        </span>
      </Button>

      {isModalOpen && groups.length > 1 && (
        <GroupSelectorModal
          groups={groups}
          onSelectGroup={handleGroupSelect}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
}
